/* eslint-disable indent */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFilterResults } from './hooks/useFilterResults';
import Svg from '../../components/react/Svg';
import { DayPickerRangeController } from 'react-dates';
import moment from 'moment';
import { START_DATE } from 'react-dates/constants';
import 'react-dates/initialize';
import { breakpoints } from '../../helpers/constants';
import { MultiSelect } from 'react-multi-select-component';
import _isEqual from 'lodash/isEqual';
import { throttle, debounce } from '../../helpers';

const View = {
  Grid: 'grid',
  List: 'list'
};

const EventListing = ({
  eventsId,
  heading,
  labelEventsFilter,
  placeholderSelected,
  placeholderEventsFilter,
  labelDate,
  placeholderDate,
  labelEventType,
  placeholderEventType,
  labelLocation,
  placeholderLocation,
  labelAudience,
  placeholderAudience,
  labelSeeEventResults,
  ariaLabelViewGrid,
  ariaLabelViewList,
  labelClearAll,
  labelViewMore,
  paginationPerPage,
  apiEndpoint,
  filterEventTypes,
  filterLocations,
  filterAudiences,
  noResultsTitle,
  noResultsCopy,
  noResultsCta
}) => {
  const [view, setView] = useState(View.Grid);
  const [showCalendar, setShowCalendar] = useState(false);
  const [events, setEvents] = useState([]);
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  const [isMobile, setIsMobile] = useState(false);
  const [daySize, setDaySize] = useState(40);
  const [isMobileModal, setIsMobileModal] = useState(false);
  const [areFiltersClosed, setAreFiltersClosed] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [eventType, setEventType] = useState([]);
  const [location, setLocation] = useState([]);
  const [audience, setAudience] = useState([]);
  const [currentFilters, setCurrentFilters] = useState({
    startDate,
    endDate,
    eventType,
    location,
    audience
  });

  const [classOpenDate, setClassOpenDate] = useState('');

  const refBtnDateFilter = useRef(null);
  const refCalendarWrapper = useRef(null);

  const filterEventTypesJson = JSON.parse(filterEventTypes);
  const filterLocationsJson = JSON.parse(filterLocations);
  const filterAudiencesJson = JSON.parse(filterAudiences);
  const noResultsCtaJson = JSON.parse(noResultsCta);

  const filterResultHook = useFilterResults({
    endpoint: apiEndpoint,
    itemsPerPage: paginationPerPage,
    eventsId
  });

  const redLineRef = useRef(null);

  const handleScroll = () => {
    redLineAnimation();
  };

  const handleResize = () => {
    redLineAnimation();
  };

  useEffect(() => {
    redLineAnimation();
  }, [events, view]);

  useEffect(() => {
    const throttleScroll = throttle(handleScroll, 5);
    window.addEventListener('scroll', throttleScroll);

    return () => {
      window.removeEventListener('scroll', throttleScroll);
    };
  }, []);

  useEffect(() => {
    const debounceResize = debounce(handleResize, 200);
    window.addEventListener('resize', debounceResize);

    return () => {
      window.removeEventListener('resize', debounceResize);
    };
  }, []);

  const redLineAnimation = () => {
    const { innerWidth } = window;

    if (innerWidth < breakpoints.tabletLandscape) return;

    const line = redLineRef.current;
    const el = redLineRef.current.parentElement;

    if (!el || !line) return;

    const blockHeight = el.clientHeight;
    let scrollPos = el.getBoundingClientRect().top - 200;

    if (scrollPos > 0) scrollPos = 0;

    scrollPos = Math.abs(scrollPos);

    const scrollPercent = (scrollPos / blockHeight) * 120;

    const minHeight = 0;
    const maxHeight = 100;

    if (scrollPercent < 0) {
      line.style.height = `${minHeight}%`;
    } else if (scrollPercent > maxHeight) {
      line.style.height = `${maxHeight}%`;
    } else {
      line.style.height = `${scrollPercent}%`;
    }
  };

  useEffect(() => {
    if (isMobileModal) {
      document.body.classList.add('body--no-scroll');
    } else {
      document.body.classList.remove('body--no-scroll');
    }
  }, [isMobileModal]);

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth } = window;

      setDaySize(
        Math.round(
          Math.max(
            35,
            Math.min(
              innerWidth < breakpoints.tabletLandscape
                ? innerWidth / 9
                : innerWidth / 20,
              innerWidth < breakpoints.tabletLandscape ? 80 : 100
            )
          )
        )
      );

      setIsMobile(innerWidth < breakpoints.tabletLandscape);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        refBtnDateFilter.current &&
        !refBtnDateFilter.current.contains(event.target) &&
        refCalendarWrapper.current &&
        !refCalendarWrapper.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [refBtnDateFilter, refCalendarWrapper]);

  useEffect(() => {
    if (filterResultHook.total > 0 && filterResultHook.page > 1) {
      setEvents([...events, ...filterResultHook.results]);
      return;
    } else if (filterResultHook.page <= 1) {
      setEvents(filterResultHook.results);
    } else {
      setEvents([]);
    }
  }, [filterResultHook.total, filterResultHook.results]);

  useEffect(() => {
    setClassOpenDate(showCalendar ? 'filter-trigger--active' : '');
    setAreFiltersClosed(!showCalendar);

    if (!showCalendar) {
      compareFiltersAndFetch();
    }
  }, [showCalendar]);

  const handleViewMore = event => {
    removeFocus(event);
    filterResultHook.handleViewMore();
  }; //handleViewMore

  const handleViewGrid = event => {
    removeFocus(event);
    setView(View.Grid);
  }; //handleViewGrid

  const handleViewList = event => {
    removeFocus(event);
    setView(View.List);
  }; //handleViewList

  const handleShowCalendar = event => {
    removeFocus(event);
    setShowCalendar(!showCalendar);
  }; //handleDateChange

  const handleClearDates = event => {
    event.preventDefault();

    setStartDate('');
    setEndDate('');

    fetchNewResultsByFilter('', '', eventType, location, audience);
  }; //handleClearDates

  const handleClearFilters = event => {
    event.preventDefault();

    setStartDate('');
    setEndDate('');
    setEventType([]);
    setLocation([]);
    setAudience([]);

    fetchNewResultsByFilter('', '', [], [], []);
  }; //handleClearFilters

  const removeFocus = event => {
    event.preventDefault();
    event.currentTarget.blur();
  }; //removeFocus

  const EventTags = ({ tags }) => {
    if (!tags) return null;
    return tags.map((tag, index) => {
      return tag ? <span key={index}>{tag}</span> : null;
    });
  }; //EventTags

  const handleOnMenuToggle = isOpen => {
    setAreFiltersClosed(!isOpen);

    if (!isOpen) {
      compareFiltersAndFetch();
    }
  }; //handleOnMenuToggle

  const compareFiltersAndFetch = () => {
    const newFiltersResult = {
      startDate,
      endDate,
      eventType,
      location,
      audience
    };

    const filtersAreTheSame = _isEqual(newFiltersResult, currentFilters);

    if (!filtersAreTheSame) {
      fetchNewResultsByFilter(
        startDate,
        endDate,
        eventType,
        location,
        audience
      );
    }
  }; //compareFiltersAndFetch

  const fetchNewResultsByFilter = (
    startDate,
    endDate,
    eventType,
    location,
    audience
  ) => {
    setEvents([]);

    setCurrentFilters({ startDate, endDate, eventType, location, audience });

    filterResultHook.setPage(1);
    filterResultHook.setStartDate(
      validateAndFormatDate(startDate, 'YYYY-MM-DD')
    );
    filterResultHook.setEndDate(validateAndFormatDate(endDate, 'YYYY-MM-DD'));
    filterResultHook.setType(eventType.map(item => item.value).join(','));
    filterResultHook.setLocation(location.map(item => item.value).join(','));
    filterResultHook.setAudience(audience.map(item => item.value).join(','));
  }; //fetchNewResultsByFilter

  const validateAndFormatDate = (inputDate, format) => {
    if (!inputDate) return null;

    const parsedDate = moment(inputDate, format, true);

    if (parsedDate.isValid()) {
      return parsedDate.format(format);
    }

    return null;
  }; //validateAndFormatDate

  return (
    <div
      className={`event-listing-module ${
        filterResultHook.loading ? 'event-listing-module--loading' : ''
      }`}
    >
      <div className="container">
        <div className="event-listing__red-line" ref={redLineRef}></div>

        <h2 className="event-listing__heading">{heading}</h2>

        <div className="event-listing__wrapper">
          <div className="event-listing__filter-mobile">
            <button
              type="button"
              className="btn btn--primary btn--outline event-listing__filter-trigger"
              onClick={() => setIsMobileModal(true)}
            >
              <span>{labelEventsFilter}</span>
              <span>{placeholderEventsFilter}</span>
            </button>
          </div>

          <div
            className={`event-listing__filter-wrapper ${
              isMobileModal ? 'event-listing__filter-wrapper--visible' : ''
            }`}
          >
            <button
              className="event-listing__filter-close"
              type="button"
              onClick={() => setIsMobileModal(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="none"
              >
                <title>close filter</title>
                <path
                  d="M5.09021 12.9554L8.03966 10.0059L11.0843 13.0505L13.0506 11.0842L10.006 8.03964L12.9554 5.09019L10.9098 3.0446L7.96038 5.99405L4.91578 2.94945L2.94948 4.91576L5.99407 7.96036L3.04462 10.9098L5.09021 12.9554Z"
                  fill="#F7F9FB"
                />
              </svg>
            </button>

            <div className="event-listing__filter">
              <div>
                <button
                  ref={refBtnDateFilter}
                  type="button"
                  className={`btn btn--primary btn--outline event-listing__filter-trigger ${classOpenDate}`}
                  onClick={handleShowCalendar}
                >
                  <span>
                    {labelDate}
                    <Svg
                      viewBox="0 0 20 20"
                      name="booking-calendar"
                      altText="filter button"
                    />
                  </span>
                  {startDate || endDate ? (
                    <span>
                      {startDate ? moment(startDate).format('MMM D') : ''}
                      {startDate && endDate ? ' - ' : ''}
                      {endDate ? moment(endDate).format('MMM D') : ''}
                    </span>
                  ) : (
                    <span>{placeholderDate}</span>
                  )}
                </button>

                {showCalendar && (
                  <div
                    ref={refCalendarWrapper}
                    className="event-listing__filter-calendar"
                    style={{
                      height: isMobile ? 'calc(100vh - 100px)' : 'initial'
                    }}
                  >
                    <DayPickerRangeController
                      startDate={startDate}
                      endDate={endDate}
                      onDatesChange={({ startDate, endDate }) => {
                        setStartDate(startDate);
                        setEndDate(endDate);
                      }}
                      focusedInput={focusedInput}
                      onFocusChange={focusedInput =>
                        setFocusedInput(focusedInput || START_DATE)
                      }
                      initialVisibleMonth={null}
                      hideKeyboardShortcutsPanel={true}
                      numberOfMonths={isMobile ? 12 : 2}
                      orientation={
                        isMobile ? 'verticalScrollable' : 'horizontal'
                      }
                      minimumNights={0}
                      isOutsideRange={day => day.isBefore(moment())}
                      navPrev={
                        isMobile ? (
                          <></>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 4 10"
                              fill="none"
                            >
                              <path
                                d="M0.476013 5.54993L2.98432 10L3.8335 8.90065L1.63437 4.99999L3.8335 1.09935L2.98432 0L0.476013 4.45027L0.166832 5L0.476013 5.54993Z"
                                fill="#111111"
                              />
                            </svg>
                          </>
                        )
                      }
                      navNext={
                        isMobile ? (
                          <></>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 4 10"
                              fill="none"
                            >
                              <path
                                d="M3.52399 4.45056L1.01568 0.000244141L0.166504 1.0996L2.36563 5.00025L0.166504 8.90089L1.01568 10.0002L3.52399 5.54993L3.83317 5.00025L3.52399 4.45056Z"
                                fill="#111111"
                              />
                            </svg>
                          </>
                        )
                      }
                      daySize={daySize}
                      transitionDuration={0}
                    />

                    {startDate || endDate ? (
                      <div className="event-listing__filter-calendar__clear-all">
                        <button type="button" onClick={handleClearDates}>
                          {labelClearAll}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <MultiSelect
                options={filterEventTypesJson}
                value={eventType}
                onChange={setEventType}
                disableSearch={true}
                ClearSelectedIcon={null}
                hasSelectAll={false}
                valueRenderer={() => {
                  return (
                    <>
                      <span>{labelEventType}</span>
                      {eventType.length > 0 ? (
                        <span>
                          {eventType.length} {placeholderSelected}
                        </span>
                      ) : (
                        <span>{placeholderEventType}</span>
                      )}
                    </>
                  );
                }}
                className="multi-select multi-select--event-type"
                onMenuToggle={handleOnMenuToggle}
              />

              <MultiSelect
                options={filterLocationsJson}
                value={location}
                onChange={setLocation}
                disableSearch={true}
                ClearSelectedIcon={null}
                hasSelectAll={false}
                valueRenderer={() => {
                  return (
                    <>
                      <span>{labelLocation}</span>
                      {location.length > 0 ? (
                        <span>
                          {location.length} {placeholderSelected}
                        </span>
                      ) : (
                        <span>{placeholderLocation}</span>
                      )}
                    </>
                  );
                }}
                className="multi-select multi-select--location"
                onMenuToggle={handleOnMenuToggle}
              />

              <MultiSelect
                options={filterAudiencesJson}
                value={audience}
                onChange={setAudience}
                disableSearch={true}
                ClearSelectedIcon={null}
                hasSelectAll={false}
                valueRenderer={() => {
                  return (
                    <>
                      <span>{labelAudience}</span>
                      {audience.length > 0 ? (
                        <span>
                          {audience.length} {placeholderSelected}
                        </span>
                      ) : (
                        <span>{placeholderAudience}</span>
                      )}
                    </>
                  );
                }}
                className="multi-select multi-select--audience"
                onMenuToggle={handleOnMenuToggle}
              />
            </div>

            <div className="event-listing__view">
              <button
                type="button"
                className={`btn btn--primary ${
                  view === View.Grid ? '' : 'btn--outline'
                }`}
                onClick={handleViewGrid}
                aria-label={ariaLabelViewGrid}
              >
                <Svg
                  viewBox="0 0 26 26"
                  name="view-grid"
                  altText="change view"
                />
              </button>

              <button
                type="button"
                className={`btn btn--primary ${
                  view === View.List ? '' : 'btn--outline'
                }`}
                onClick={handleViewList}
                aria-label={ariaLabelViewList}
              >
                <Svg
                  style={{ width: 30 }}
                  viewBox="0 0 26 26"
                  name="view-list"
                  altText="change view"
                />
              </button>
            </div>

            <div className="event-listing__filter-footer">
              {(startDate ||
                endDate ||
                eventType.length > 0 ||
                location.length > 0 ||
                audience.length > 0) &&
              areFiltersClosed === true ? (
                <button
                  type="button"
                  className="event-listing__filter-footer-clear"
                  onClick={handleClearFilters}
                >
                  {labelClearAll}
                </button>
              ) : null}

              <button
                type="button"
                className="btn btn--primary btn--outline"
                onClick={() => setIsMobileModal(false)}
              >
                {labelSeeEventResults}
              </button>
            </div>
          </div>

          {startDate ||
          endDate ||
          eventType.length > 0 ||
          location.length > 0 ||
          audience.length > 0 ? (
            <div className="event-listing__tags">
              <div className="event-listing__labels">
                {startDate || endDate ? (
                  <div className="event-listing__label">
                    <span>
                      {startDate ? moment(startDate).format('MMM D') : ''}
                      {startDate && endDate ? ' - ' : ''}
                      {endDate ? moment(endDate).format('MMM D') : ''}
                    </span>
                    <button type="button" onClick={handleClearDates}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M12.4721 4.47108C12.7324 4.21073 12.7324 3.78862 12.4721 3.52827C12.2117 3.26792 11.7896 3.26792 11.5292 3.52827L8.00065 7.05687L4.47206 3.52827C4.21171 3.26792 3.7896 3.26792 3.52925 3.52827C3.2689 3.78862 3.2689 4.21073 3.52925 4.47108L7.05784 7.99967L3.52925 11.5283C3.2689 11.7886 3.2689 12.2107 3.52925 12.4711C3.7896 12.7314 4.21171 12.7314 4.47206 12.4711L8.00065 8.94248L11.5292 12.4711C11.7896 12.7314 12.2117 12.7314 12.4721 12.4711C12.7324 12.2107 12.7324 11.7886 12.4721 11.5283L8.94346 7.99967L12.4721 4.47108Z"
                          fill="#F7F9FB"
                        />
                      </svg>
                    </button>
                  </div>
                ) : null}

                {eventType.length > 0 ? (
                  <>
                    {eventType.map((item, index) => (
                      <div className="event-listing__label" key={index}>
                        <span>{item.label}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newEventType = eventType.filter(
                              i => i.value !== item.value
                            );
                            setEventType(newEventType);
                            filterResultHook.setType(
                              newEventType.map(i => i.value).join(',')
                            );
                            setEvents([]);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M12.4721 4.47108C12.7324 4.21073 12.7324 3.78862 12.4721 3.52827C12.2117 3.26792 11.7896 3.26792 11.5292 3.52827L8.00065 7.05687L4.47206 3.52827C4.21171 3.26792 3.7896 3.26792 3.52925 3.52827C3.2689 3.78862 3.2689 4.21073 3.52925 4.47108L7.05784 7.99967L3.52925 11.5283C3.2689 11.7886 3.2689 12.2107 3.52925 12.4711C3.7896 12.7314 4.21171 12.7314 4.47206 12.4711L8.00065 8.94248L11.5292 12.4711C11.7896 12.7314 12.2117 12.7314 12.4721 12.4711C12.7324 12.2107 12.7324 11.7886 12.4721 11.5283L8.94346 7.99967L12.4721 4.47108Z"
                              fill="#F7F9FB"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </>
                ) : null}

                {location.length > 0 ? (
                  <>
                    {location.map((item, index) => (
                      <div className="event-listing__label" key={index}>
                        <span>{item.label}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newLocation = location.filter(
                              i => i.value !== item.value
                            );
                            setLocation(newLocation);
                            filterResultHook.setLocation(
                              newLocation.map(i => i.value).join(',')
                            );
                            setEvents([]);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M12.4721 4.47108C12.7324 4.21073 12.7324 3.78862 12.4721 3.52827C12.2117 3.26792 11.7896 3.26792 11.5292 3.52827L8.00065 7.05687L4.47206 3.52827C4.21171 3.26792 3.7896 3.26792 3.52925 3.52827C3.2689 3.78862 3.2689 4.21073 3.52925 4.47108L7.05784 7.99967L3.52925 11.5283C3.2689 11.7886 3.2689 12.2107 3.52925 12.4711C3.7896 12.7314 4.21171 12.7314 4.47206 12.4711L8.00065 8.94248L11.5292 12.4711C11.7896 12.7314 12.2117 12.7314 12.4721 12.4711C12.7324 12.2107 12.7324 11.7886 12.4721 11.5283L8.94346 7.99967L12.4721 4.47108Z"
                              fill="#F7F9FB"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </>
                ) : null}

                {audience.length > 0 ? (
                  <>
                    {audience.map((item, index) => (
                      <div className="event-listing__label" key={index}>
                        <span>{item.label}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newAudience = audience.filter(
                              i => i.value !== item.value
                            );
                            setAudience(newAudience);
                            filterResultHook.setAudience(
                              newAudience.map(i => i.value).join(',')
                            );
                            setEvents([]);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M12.4721 4.47108C12.7324 4.21073 12.7324 3.78862 12.4721 3.52827C12.2117 3.26792 11.7896 3.26792 11.5292 3.52827L8.00065 7.05687L4.47206 3.52827C4.21171 3.26792 3.7896 3.26792 3.52925 3.52827C3.2689 3.78862 3.2689 4.21073 3.52925 4.47108L7.05784 7.99967L3.52925 11.5283C3.2689 11.7886 3.2689 12.2107 3.52925 12.4711C3.7896 12.7314 4.21171 12.7314 4.47206 12.4711L8.00065 8.94248L11.5292 12.4711C11.7896 12.7314 12.2117 12.7314 12.4721 12.4711C12.7324 12.2107 12.7324 11.7886 12.4721 11.5283L8.94346 7.99967L12.4721 4.47108Z"
                              fill="#F7F9FB"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </>
                ) : null}
              </div>

              <div
                className="event-listing__clear-all"
                onClick={handleClearFilters}
              >
                {labelClearAll}
              </div>
            </div>
          ) : null}

          <div className="event-listing__events-wrapper">
            {filterResultHook.loading && (
              <div className="event-listing__loading">
                <div className="lds-ring">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            )}

            {filterResultHook.total > 0 && (
              <div
                className={`event-listing__events ${
                  view === View.List ? 'event-listing__events--list' : ''
                }`}
              >
                {events.map((event, index) => (
                  <a
                    href={event.link}
                    key={index}
                    className="event-listing__event"
                  >
                    <div className="event-listing__event-image">
                      {event.location && (
                        <span className="event-listing__event-location">
                          {event.location}
                        </span>
                      )}
                      <picture>
                        <source
                          media="(min-width: 420px) and (max-width: 1024px)"
                          srcSet={event.image.srcSet}
                        />
                        <img src={event.image.src} alt={event.image.alt} />
                      </picture>
                    </div>

                    <div className="event-listing__event-content">
                      <h3>{event.title}</h3>
                      <div className="event-listing__event-tags">
                        <EventTags tags={event.dates} />
                        <EventTags tags={event.types} />
                        <EventTags tags={event.audiences} />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {filterResultHook.total === 0 && !filterResultHook.loading && (
              <div className="event-listing__events_no-results">
                {noResultsTitle && <h3>{noResultsTitle}</h3>}
                {noResultsCopy && (
                  <div dangerouslySetInnerHTML={{ __html: noResultsCopy }} />
                )}
                {noResultsCtaJson && (
                  <a
                    href={noResultsCtaJson.url}
                    className="logo-row__cta"
                    target={noResultsCtaJson.target}
                  >
                    {noResultsCtaJson.label}
                  </a>
                )}
              </div>
            )}
          </div>

          {filterResultHook.viewMore && !filterResultHook.loading && (
            <div className="event-listing__more">
              <button
                type="button"
                className="btn btn--primary btn--outline"
                onClick={handleViewMore}
              >
                {labelViewMore}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EventListing.propTypes = {
  eventsId: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  labelEventsFilter: PropTypes.string.isRequired,
  placeholderSelected: PropTypes.string.isRequired,
  placeholderEventsFilter: PropTypes.string.isRequired,
  labelDate: PropTypes.string.isRequired,
  placeholderDate: PropTypes.string.isRequired,
  labelEventType: PropTypes.string.isRequired,
  placeholderEventType: PropTypes.string.isRequired,
  labelLocation: PropTypes.string.isRequired,
  placeholderLocation: PropTypes.string.isRequired,
  labelAudience: PropTypes.string.isRequired,
  labelSeeEventResults: PropTypes.string.isRequired,
  placeholderAudience: PropTypes.string.isRequired,
  ariaLabelViewGrid: PropTypes.string.isRequired,
  ariaLabelViewList: PropTypes.string.isRequired,
  labelClearAll: PropTypes.string.isRequired,
  labelViewMore: PropTypes.string.isRequired,
  paginationPerPage: PropTypes.string.isRequired,
  apiEndpoint: PropTypes.string.isRequired,
  filterEventTypes: PropTypes.string.isRequired,
  filterLocations: PropTypes.string.isRequired,
  filterAudiences: PropTypes.string.isRequired,
  noResultsTitle: PropTypes.string.isRequired,
  noResultsCopy: PropTypes.string.isRequired,
  noResultsCta: PropTypes.string.isRequired
};

export default EventListing;
